import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateTicket } from '@/hooks/useTickets';
import { calculatePriority, FORM_ASSIGNMENTS } from '../../../../../shared/form-config';
import { useTranslation } from 'react-i18next';
import { ReporterInfoFields } from '@/components/tickets/ReporterInfoFields';
import { TicketFormButtons } from '@/components/tickets/TicketFormButtons';

const userOffboardingSchema = z.object({
  firstName: z.string().min(1, 'El nombre es requerido'),
  lastName: z.string().min(1, 'El apellido es requerido'),
  endDate: z.string().min(1, 'La fecha de salida es requerida'),
  endTime: z.string().optional(),
  additionalComments: z.string().optional(),
});

type UserOffboardingFormData = z.infer<typeof userOffboardingSchema>;

export function UserOffboardingForm() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors } } = useForm<UserOffboardingFormData>({
    resolver: zodResolver(userOffboardingSchema),
  });

  // ✨ Using tRPC mutation
  const createTicketMutation = useCreateTicket();
  const onSubmit = async (data: UserOffboardingFormData) => {
    const priority = calculatePriority('user-offboarding', data);
    const ticketData = {
      type: 'user-offboarding',
      title: `Baja de Usuario: ${data.firstName} ${data.lastName}`,
      formData: {
        reporterName: user?.name,
        reporterEmail: user?.email,
        ...data,
      },
      attachments: [],
      priority,
      assignedTo: FORM_ASSIGNMENTS['user-offboarding'],
    };
    createTicketMutation.mutate(ticketData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('tickets.useroffboardingform.baja_de_usuario')}</CardTitle>
        <CardDescription>
          {t('tickets.useroffboardingform.dar_de_baja_colaborador_prioridad_alta')} {FORM_ASSIGNMENTS['user-offboarding']}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <ReporterInfoFields name={user?.name} email={user?.email} />

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input id="firstName" {...register('firstName')} />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                Apellido <span className="text-destructive">*</span>
              </Label>
              <Input id="lastName" {...register('lastName')} />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="endDate">
                {t('tickets.useroffboardingform.fecha_de_salida')} <span className="text-destructive">*</span>
              </Label>
              <Input id="endDate" type="date" {...register('endDate')} />
              {errors.endDate && (
                <p className="text-sm text-destructive">{errors.endDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime">{t('tickets.useroffboardingform.hora_de_salida_opcional')}</Label>
              <Input id="endTime" type="time" {...register('endTime')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="additionalComments">{t('forms.additionalComments')}</Label>
            <Textarea
              id="additionalComments"
              placeholder={t('forms.additionalCommentsPlaceholder')}
              rows={4}
              {...register('additionalComments')}
            />
          </div>

          <TicketFormButtons isPending={createTicketMutation.isPending} />
        </form>
      </CardContent>
    </Card>
  );
}
